import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  useWindowDimensions,
  Platform,
  Pressable,
} from 'react-native';
import {
  Text,
  Button,
  Surface,
  TextInput,
  ActivityIndicator,
  Dialog,
  Portal,
} from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import authService from '../services/authService';
import helpService, { HelpRequest } from '../services/helpService';
import SlidingMenu from '../components/SlidingMenu';
import { RootStackParamList } from '../types/navigation';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type HomeScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Home'>;
};

const HomeScreen: React.FC<HomeScreenProps> = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const isHome = navigation.getState?.().routes[navigation.getState().index]?.name === 'Home';

  const [userName, setUserName] = useState<string>('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [helpTitle, setHelpTitle] = useState<string>('');
  const [helpText, setHelpText] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<HelpRequest['category'] | ''>('');
  const [activeTab, setActiveTab] = useState<'pending' | 'recent' | 'offered'>('pending');
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCategoryMenuVisible, setIsCategoryMenuVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const categories: HelpRequest['category'][] = [
    'Classroom Management',
    'Curriculum & Instruction',
    'Assessment & Evaluation',
    'Technology Integration',
    'Special Education',
    'Family Communication',
    'Professional Growth',
    'Other Topics',
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setUserName(user.firstName);
          const userId = user.id || user._id;
          if (userId) {
            setCurrentUserId(userId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    fetchHelpRequests();
  }, [activeTab]);

  const fetchHelpRequests = async () => {
    try {
      setIsLoading(true);
      const requests = await helpService.getHelpRequests(activeTab);
      setHelpRequests(requests);

      const counts: Record<string, number> = {};
      for (const request of requests) {
        try {
          const responses = await helpService.getResponses(request._id);
          counts[request._id] = responses.length;
        } catch (error) {
          console.warn(`Failed to fetch responses for request ${request._id}:`, error);
          counts[request._id] = 0;
        }
      }
      setResponseCounts(counts);
    } catch (error) {
      console.error('Error fetching help requests:', error);
      Alert.alert('Error', 'Failed to fetch help requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigation.replace('Login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAskForHelp = async () => {
    if (!helpTitle.trim() || !helpText.trim() || !selectedCategory) {
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
      setIsCategoryMenuVisible(false);
      return;
    }

    try {
      setIsSubmitting(true);
      await helpService.createHelpRequest({
        title: helpTitle,
        description: helpText,
        category: selectedCategory,
      });

      setHelpTitle('');
      setHelpText('');
      setSelectedCategory('');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      fetchHelpRequests();
    } catch (error: unknown) {
      console.error('Failed to create help request:', error);
      const ax = error as {
        response?: { data?: { message?: string; error?: string; details?: string[] } };
      };
      let errorMessage = 'Failed to create help request. Please try again.';

      if (ax.response?.data) {
        const { message, error: errMsg, details } = ax.response.data;
        errorMessage = `${message}: ${errMsg}`;
        if (details && details.length > 0) {
          errorMessage += `\n\nDetails: ${details.join(', ')}`;
        }
      }

      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    try {
      await helpService.deleteHelpRequest(requestId);
      await fetchHelpRequests();
      setDeleteDialogVisible(false);
      setRequestToDelete(null);
      Alert.alert('Success', 'Help request deleted successfully');
    } catch (error: unknown) {
      console.error('Error in handleDeleteRequest:', error);
      const ax = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        ax.response?.data?.message || 'Failed to delete help request. Please try again.';
      Alert.alert('Error', errorMessage);
    }
  };

  const showDeleteDialog = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogVisible(true);
  };

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const handleDescriptionChange = (text: string) => {
    setHelpText(text);
  };

  const renderHelpRequest = (item: HelpRequest) => {
    const isAuthor = currentUserId && item.user._id === currentUserId;

    return (
      <Surface key={item._id} style={styles.helpRequestCard} elevation={1}>
        <View style={styles.helpRequestHeader}>
          <Text style={styles.helpRequestTitle}>{item.title}</Text>
          {isAuthor && (
            <TouchableOpacity onPress={() => showDeleteDialog(item._id)} style={styles.deleteButton}>
              <Text style={styles.deleteText}>Delete</Text>
            </TouchableOpacity>
          )}
        </View>
        {item.category ? (
          <Text style={styles.helpRequestCategory}>{item.category}</Text>
        ) : null}
        <Text style={styles.helpRequestDescription}>{item.description}</Text>
        <View style={styles.helpRequestFooter}>
          <Text style={styles.helpRequestAuthor}>{item.user.displayName}</Text>
          <Text style={styles.helpRequestDate}>
            {new Date(item.createdAt).toLocaleDateString()}
          </Text>
        </View>
        <View style={styles.responseInfo}>
          <Text style={styles.responseCount}>
            {responseCounts[item._id] || 0}{' '}
            {responseCounts[item._id] === 1 ? 'response' : 'responses'}
          </Text>
          <Button
            mode="contained"
            onPress={() => navigation.navigate('ViewResponses', { helpRequest: item })}
            style={styles.viewButton}
            buttonColor={brandButton}
            textColor="#ffffff"
            labelStyle={styles.viewButtonLabel}
          >
            View
          </Button>
        </View>
      </Surface>
    );
  };

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            buttonLabel="Logout"
            onButtonPress={handleLogout}
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            style={styles.headerBar}
            onLogoPress={() => navigation.navigate('Home')}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <ScrollView
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.heroSection}>
              <Text style={styles.title}>Welcome, {userName || 'User'} 👋</Text>
              <Text style={styles.subtitle}>
                You're in a safe space to ask for support anonymously.
              </Text>
            </View>

            <View style={styles.toggleButtonRow}>
              <Pressable
                style={[styles.toggleButton, isHome && styles.toggleButtonActive]}
                onPress={() => navigation.navigate('Home')}
              >
                <Text style={[styles.toggleButtonText, isHome && styles.toggleButtonTextActive]}>
                  Ask for Help
                </Text>
              </Pressable>
              <Pressable
                style={[styles.toggleButton, !isHome && styles.toggleButtonActive]}
                onPress={() => navigation.navigate('AllHelpTickets' as never)}
              >
                <Text style={[styles.toggleButtonText, !isHome && styles.toggleButtonTextActive]}>
                  Offer Help
                </Text>
              </Pressable>
            </View>

            <Surface style={styles.helpContainer} elevation={0}>
              <Text style={styles.formSectionLabel}>New support request</Text>
              <TextInput
                mode="outlined"
                value={helpTitle}
                onChangeText={setHelpTitle}
                style={styles.helpInput}
                placeholder="Add a title for your support request"
                placeholderTextColor="#94A3B8"
                outlineColor="rgba(15, 23, 42, 0.12)"
                activeOutlineColor={brandPrimary}
              />
              <TextInput
                mode="outlined"
                value={helpText}
                onChangeText={handleDescriptionChange}
                multiline
                style={[styles.helpInput, styles.descriptionInput]}
                placeholder="Describe what you need help with"
                placeholderTextColor="#94A3B8"
                outlineColor="rgba(15, 23, 42, 0.12)"
                activeOutlineColor={brandPrimary}
              />
              <Text style={styles.anonymousMessage}>
                Your message will be anonymous to the community
              </Text>

              <View style={styles.categoryDropdownContainer}>
                <TouchableOpacity
                  style={styles.categorySelector}
                  onPress={() => setIsCategoryMenuVisible(!isCategoryMenuVisible)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.categorySelectorText,
                      !selectedCategory && styles.categorySelectorPlaceholder,
                    ]}
                  >
                    {selectedCategory || 'Select a help category'}
                  </Text>
                  <Text
                    style={[
                      styles.categorySelectorIcon,
                      isCategoryMenuVisible && styles.categorySelectorIconRotated,
                    ]}
                  >
                    ▼
                  </Text>
                </TouchableOpacity>

                {isCategoryMenuVisible && (
                  <View style={styles.categoryDropdown}>
                    {categories.map((category) => (
                      <TouchableOpacity
                        key={category}
                        style={styles.categoryOption}
                        onPress={() => {
                          setSelectedCategory(category);
                          setIsCategoryMenuVisible(false);
                        }}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.categoryOptionText}>{category}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>

              <Button
                mode="contained"
                onPress={handleAskForHelp}
                style={styles.helpButton}
                buttonColor={brandButton}
                textColor="#ffffff"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Post Request
              </Button>

              {showSuccess && (
                <Text style={styles.successMessage}>Your help request has been submitted!</Text>
              )}
              {showError && (
                <Text style={styles.errorMessage}>Please fill out all fields to submit</Text>
              )}
            </Surface>

            <View style={styles.requestsSection}>
              <Text style={styles.requestsSectionTitle}>Your requests</Text>
              <View style={styles.tabsContainer}>
                <TouchableOpacity
                  style={[styles.tabPill, activeTab === 'pending' && styles.tabPillActive]}
                  onPress={() => setActiveTab('pending')}
                >
                  <Text style={[styles.tabText, activeTab === 'pending' && styles.tabTextActive]}>
                    Unanswered
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.tabPill, activeTab === 'recent' && styles.tabPillActive]}
                  onPress={() => setActiveTab('recent')}
                >
                  <Text style={[styles.tabText, activeTab === 'recent' && styles.tabTextActive]}>
                    Answered
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.tabPill, activeTab === 'offered' && styles.tabPillActive]}
                  onPress={() => setActiveTab('offered')}
                >
                  <Text style={[styles.tabText, activeTab === 'offered' && styles.tabTextActive]}>
                    Offered
                  </Text>
                </TouchableOpacity>
              </View>

              {isLoading ? (
                <View style={styles.listLoadingContainer}>
                  <ActivityIndicator size="large" color={brandPrimary} />
                  <Text style={styles.loadingText}>Loading help requests…</Text>
                </View>
              ) : helpRequests.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyEmoji}>🌿</Text>
                  <Text style={styles.emptyTitle}>No requests here yet</Text>
                  <Text style={styles.emptyText}>
                    Post a support request above, or check another tab for past activity.
                  </Text>
                </View>
              ) : (
                <View style={styles.ticketList}>{helpRequests.map(renderHelpRequest)}</View>
              )}
            </View>
          </ScrollView>
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />

      <Portal>
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Delete help request</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.dialogText}>Are you sure you want to delete this help request?</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)} textColor={brandPrimary}>
              Cancel
            </Button>
            <Button
              onPress={() => requestToDelete && handleDeleteRequest(requestToDelete)}
              textColor="#d32f2f"
            >
              Delete
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  contentContainer: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  heroSection: {
    marginBottom: 24,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 10,
    color: '#1E293B',
    letterSpacing: -0.5,
    lineHeight: 40,
  },
  subtitle: {
    fontSize: 17,
    color: '#64748B',
    lineHeight: 26,
  },
  toggleButtonRow: {
    flexDirection: 'row',
    width: '100%',
    marginBottom: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 999,
    padding: 4,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
          elevation: 1,
        }),
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { transition: 'background-color 0.2s ease' } : {}),
  },
  toggleButtonActive: {
    backgroundColor: brandButton,
  },
  toggleButtonText: {
    color: '#64748B',
    fontWeight: '600',
    fontSize: 15,
  },
  toggleButtonTextActive: {
    color: '#FFFFFF',
  },
  helpContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    marginBottom: 32,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  formSectionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 16,
  },
  helpInput: {
    marginBottom: 14,
    backgroundColor: '#FFFFFF',
  },
  descriptionInput: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  helpButton: {
    borderRadius: 999,
    alignSelf: 'center',
    paddingHorizontal: 28,
    marginTop: 4,
  },
  anonymousMessage: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 14,
    lineHeight: 20,
  },
  categoryDropdownContainer: {
    marginBottom: 16,
  },
  categorySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.1)',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  categorySelectorText: {
    fontSize: 15,
    color: '#1E293B',
    flex: 1,
    paddingRight: 8,
  },
  categorySelectorPlaceholder: {
    color: '#94A3B8',
  },
  categorySelectorIcon: {
    fontSize: 11,
    color: '#64748B',
  },
  categorySelectorIconRotated: {
    transform: [{ rotate: '180deg' }],
  },
  categoryDropdown: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
    borderRadius: 12,
    marginTop: 8,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.08,
          shadowRadius: 12,
          elevation: 4,
        }),
  },
  categoryOption: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(15, 23, 42, 0.05)',
  },
  categoryOptionText: {
    fontSize: 15,
    color: '#334155',
  },
  successMessage: {
    color: brandPrimary,
    textAlign: 'center',
    marginTop: 14,
    fontSize: 15,
    fontWeight: '600',
  },
  errorMessage: {
    color: '#d32f2f',
    textAlign: 'center',
    marginTop: 14,
    fontSize: 15,
    fontWeight: '600',
  },
  requestsSection: {
    marginTop: 4,
  },
  requestsSectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 14,
  },
  tabsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  tabPill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
  },
  tabPillActive: {
    backgroundColor: brandButton,
    borderColor: brandButton,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  tabTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  listLoadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    textAlign: 'center',
    color: '#64748B',
    marginTop: 12,
    fontSize: 15,
  },
  emptyContainer: {
    alignItems: 'center',
    padding: 32,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
  },
  emptyEmoji: {
    fontSize: 36,
    marginBottom: 10,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: 15,
    lineHeight: 22,
    maxWidth: 300,
  },
  ticketList: {
    gap: 14,
    marginBottom: 16,
  },
  helpRequestCard: {
    backgroundColor: '#FFFFFF',
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    marginBottom: 0,
  },
  helpRequestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 8,
  },
  helpRequestTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    flex: 1,
    lineHeight: 26,
  },
  helpRequestCategory: {
    fontSize: 12,
    fontWeight: '600',
    color: brandPrimary,
    backgroundColor: brandPrimarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    alignSelf: 'flex-start',
    marginBottom: 10,
    overflow: 'hidden',
  },
  helpRequestDescription: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 14,
  },
  helpRequestFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  helpRequestAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  helpRequestDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  deleteButton: {
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  deleteText: {
    color: '#d32f2f',
    fontWeight: '600',
    fontSize: 13,
  },
  responseInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  responseCount: {
    fontSize: 14,
    color: '#64748B',
  },
  viewButton: {
    borderRadius: 999,
  },
  viewButtonLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  dialogText: {
    color: '#475569',
    lineHeight: 22,
  },
});

export default HomeScreen;
